import { delay } from '@/shared/lib/delay'
import { hapticFeedback } from '@telegram-apps/sdk-react'
import { toast as sonnerToast } from 'sonner'

type Feedback = 'success' | 'error' | 'warning' | 'info'

type Toaster = {
  [K in Feedback]: (...params: Parameters<typeof sonnerToast.info>) => void
}

type ToastParams = {
  message: Parameters<typeof sonnerToast.info>[0]
  data?: Parameters<typeof sonnerToast.info>[1]
  feedback: Feedback
}

const toastsQueue: ToastParams[] = []

const queueManager = () => {
  let last: Promise<void> | null = null

  const processQuee = () => {
    if (last || toastsQueue.length === 0) return
    const { feedback, message, data } = toastsQueue.shift()!

    let resolver: (value: unknown) => void

    last = new Promise((res) => {
      resolver = res
      delay(data!.duration!).then(() => {
        res(undefined)
      })
    })

    const onDismiss: Required<Exclude<typeof data, undefined>>['onDismiss'] = (
      toast,
    ) => {
      data?.onDismiss?.(toast)
      resolver(undefined)
    }

    sonnerToast[feedback](message, { ...data, onDismiss })
    hapticFeedback.impactOccurred('light')

    last.then(() => {
      last = null

      processQuee()
    })
  }

  const addToQuee = ({ feedback, message, data }: ToastParams) => {
    const duration = data?.duration ?? 4000
    const toastData = {
      ...data,
      duration,
    } as Required<Parameters<typeof sonnerToast.info>>[1]

    toastsQueue.push({ feedback, message, data: toastData })

    processQuee()
  }

  return addToQuee
}

const addToQuee = queueManager()

export const toast: Toaster = {
  success: (message, data) => addToQuee({ feedback: 'success', message, data }),
  error: (message, data) => addToQuee({ feedback: 'error', message, data }),
  info: (message, data) => addToQuee({ feedback: 'info', message, data }),
  warning: (message, data) => addToQuee({ feedback: 'warning', message, data }),
}
